import React, { useEffect, useMemo, useState } from "react";
import Table from "components/Table";
import Tooltip from "components/Tooltip";
import useStudySnapshotFields from "components/StudySnapshot/hooks/useStudySnapshotFields";
import {
  displayFieldList,
  formatStudySnapshotValue,
} from "helpers/studyDetail";
import { useInView } from "hooks/useInView";
import Link from "next/link";
import {
  SEARCH_RESULT_TABLE_COLUMNS,
  PRO_USER_SNAPSHOT_AUTO_FETCH_LIMIT,
  DEEP_USER_SNAPSHOT_AUTO_FETCH_LIMIT,
  FREE_USER_SNAPSHOT_AUTO_FETCH_LIMIT,
} from "../../constants";
import { SearchResultViewProps } from "../../types";
import PaperTitle from "../shared/PaperTitle";
import SearchResultPaperMetadata from "../shared/PaperMetadata";
import PaperActions from "../shared/PaperActions";
import { ResultFormat } from "components/Button/ResultFormatButton";
import useSubscription from "features/User/hooks/useSubscription";
import Shimmer from "components/Shimmer";
import useLabels from "hooks/useLabels";

/**
 * @component SearchResultTableRow
 * @description Table row view for search results with study snapshot data
 */
export const SearchResultTableRow = ({
  paper,
  index,
  text,
  relativeLink,
  absoluteLink,
  externalLink,
  openDetailsInNewTab,
  onResultClick,
  onClickItem,
  textType,
  badges,
  extraTags,
  showPaperDoi,
  isProcessingMetadata,
  hideCitations,
  showSourceCheckbox,
  showSaveBtn,
  showCiteBtn,
  showShareBtn,
  isLoadingExtractedAnswer,
  mixpanelActionSource,
  onClickSave,
  onClickCite,
  onCitePaper,
  onFullTextClick,
  onSharePaper,
  ...titleProps
}: SearchResultViewProps) => {
  const [labels] = useLabels("general");

  const { paper_id } = paper;

  // Memoize badges to prevent unnecessary re-renders if item object changes but badges content is the same
  const memoizedBadges = useMemo(
    () => badges,
    [badges?.sample_size, badges?.study_count]
  );

  const [ref, isInView] = useInView<HTMLTableRowElement>({
    rootMargin: "-250px", // Account for the header and input field, with some margin
  });

  const { isFreePlan, subscriptionTier } = useSubscription();
  const isDeepPlan = subscriptionTier === "deep";

  // // Auto-fetch if in view and index < user's plan for auto fetch limit
  const shouldAutoFetchSnapshot = useMemo(() => {
    // Check against the user's plan
    const userAutoFetchLimit = isFreePlan
      ? FREE_USER_SNAPSHOT_AUTO_FETCH_LIMIT
      : isDeepPlan
        ? DEEP_USER_SNAPSHOT_AUTO_FETCH_LIMIT
        : PRO_USER_SNAPSHOT_AUTO_FETCH_LIMIT;

    return isInView && index < userAutoFetchLimit;
  }, [isFreePlan, isInView, index]);

  const { fields, isLoading } = useStudySnapshotFields({
    paperId: paper_id,
    badges: memoizedBadges,
    fetchOnMount: shouldAutoFetchSnapshot,
    spendCredit: false,
  });

  // Update snapshot columns as fields are fetched
  const snapshotColumns = useMemo(() => {
    if (!fields) {
      return SEARCH_RESULT_TABLE_COLUMNS;
    }

    return SEARCH_RESULT_TABLE_COLUMNS.map((column) => {
      // If this column corresponds to a study snapshot field, add the value
      if (
        column.id !== "paper" &&
        column.id !== "answer" &&
        fields[column.id as keyof typeof fields]
      ) {
        return {
          ...column,
          value: fields[column.id as keyof typeof fields],
        };
      }
      return column;
    });
  }, [fields]);

  return (
    <Table.TR className="group/row" ref={ref}>
      {snapshotColumns.map((column) => {
        if (column.id === "paper") {
          return (
            <Table.TD key={column.id} className="h-[1px] group/cell">
              <div className="flex flex-col justify-between h-full">
                <Link
                  href={relativeLink}
                  target={
                    externalLink || openDetailsInNewTab ? "_blank" : undefined
                  }
                  key={paper_id}
                  data-testid="search-result"
                  onClick={onResultClick}
                  onMouseDown={(e) => {
                    // Fire click events for left or middle mouse button clicks.
                    if (e.button === 0 || e.button === 1)
                      onClickItem?.(paper, index, textType);
                  }}
                  data-test-text={absoluteLink}
                  className="flex-1 group"
                >
                  <PaperTitle
                    {...titleProps}
                    paper={paper}
                    resultFormat={ResultFormat.TABLE}
                  />
                  <div className="@container mt-2">
                    <SearchResultPaperMetadata
                      paper={paper}
                      badges={badges}
                      extraTags={extraTags}
                      showPaperDoi={showPaperDoi}
                      isProcessingMetadata={isProcessingMetadata}
                      hideCitations={hideCitations}
                      resultFormat={ResultFormat.TABLE}
                      showSourceCheckbox={showSourceCheckbox}
                      useTwoRowLayout
                    />
                  </div>
                </Link>
                <PaperActions
                  paper={paper}
                  text={text}
                  textType={textType}
                  shareLink={absoluteLink}
                  index={index}
                  showSaveBtn={showSaveBtn}
                  showCiteBtn={showCiteBtn}
                  showShareBtn={showShareBtn && !isLoadingExtractedAnswer}
                  resultFormat={ResultFormat.TABLE}
                  mixpanelActionSource={mixpanelActionSource}
                  onClickSave={onClickSave}
                  onClickCite={onClickCite}
                  onCitePaper={onCitePaper}
                  onFullTextClick={onFullTextClick}
                  onSharePaper={onSharePaper}
                />
              </div>
            </Table.TD>
          );
        } else if (column.id === "answer") {
          return <Table.TD key={column.id}>{text}</Table.TD>;
        }

        // Render snapshot field values or default to empty state
        const displayValue = React.isValidElement(column.value)
          ? column.value
          : column.value !== undefined && column.value !== null
            ? displayFieldList(
                formatStudySnapshotValue(
                  column.id,
                  column.value as string | string[] | number
                )
              )
            : column.value;

        return (
          <Table.TD key={column.id}>
            {isLoading ? (
              <Shimmer isActive>
                <span>{labels["extracting"]}</span>
              </Shimmer>
            ) : column.value !== undefined && column.value !== null ? (
              displayValue
            ) : (
              <Tooltip content="Not found">
                <span className="text-fg-muted">—</span>
              </Tooltip>
            )}
          </Table.TD>
        );
      })}
    </Table.TR>
  );
};

export default SearchResultTableRow;
